import React from 'react';
import { ArrowDownNarrowWide, ArrowDownAZ } from 'lucide-react';
import { BANDS } from './complianceBands';

/**
 * Compliance rate broken down by one dimension (Grade / Designation /
 * Department) — the "who is behind" half of the tab.
 *
 * One bar per group, filled with that group's met / not-met split,
 * direct-labelled with
 * the met share and the headcount behind it (a 100% built on 2 people must not
 * read like a 100% built on 30). Clicking a bar filters the whole tab to that
 * group, so the donut, the table and the export follow.
 *
 * Sorted lowest-first by default: the report exists to find the groups that
 * still need chasing, and that puts them at the top.
 */
const SORTS = [
  { key: 'rate', label: 'Lowest first', icon: ArrowDownNarrowWide },
  { key: 'name', label: 'A–Z', icon: ArrowDownAZ },
];

const ComplianceBreakdown = ({
  dimensions, groupBy, onGroupBy,
  groups, sortMode, onSortMode,
  activeValue, onGroupToggle,
}) => {
  const dimension = dimensions.find((d) => d.key === groupBy);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Compliance Rate by {dimension?.label}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Click a bar to filter this tab to that {dimension?.noun}; click it again to clear.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Group-by — the dimension to slice by */}
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
            {dimensions.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => onGroupBy(d.key)}
                aria-pressed={groupBy === d.key}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  groupBy === d.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
            {SORTS.map((s) => {
              const Icon = s.icon;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => onSortMode(s.key)}
                  aria-pressed={sortMode === s.key}
                  title={`Sort: ${s.label}`}
                  className={`px-2.5 py-1.5 transition-colors ${
                    sortMode === s.key ? 'bg-gray-100 text-gray-900' : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend: always present, so identity is never colour-alone. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-4">
        {BANDS.map((band) => (
          <span key={band.key} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <span className={`w-2.5 h-2.5 rounded-sm ${band.dot}`} />
            {band.label}
          </span>
        ))}
      </div>

      <div className="mt-4 flex-1 space-y-3 max-h-[420px] overflow-y-auto pr-1">
        {groups.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center">No staff match the current filters</p>
        ) : groups.map((g) => {
          const active = activeValue === g.value;
          const segments = BANDS
            .map((band) => ({ band, value: g.counts[band.key] || 0 }))
            .filter((s) => s.value > 0);
          return (
            <button
              key={g.value}
              type="button"
              onClick={() => onGroupToggle(g.value)}
              aria-pressed={active}
              className={`w-full text-left rounded-lg px-2 py-1.5 transition-colors ${
                active ? 'bg-blue-50 ring-1 ring-blue-300' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm text-gray-800 truncate" title={g.label}>{g.label}</span>
                <span className="text-sm tabular-nums whitespace-nowrap">
                  <span className="font-semibold text-gray-900">{g.rate.toFixed(0)}%</span>
                  <span className="text-gray-500"> · {g.counts.met}/{g.total}</span>
                </span>
              </div>
              <div className="mt-1.5 h-3 w-full rounded-full bg-gray-100 overflow-hidden flex">
                {segments.map((s, i) => (
                  <div
                    key={s.band.key}
                    className={s.band.bar}
                    title={`${s.band.label}: ${s.value} of ${g.total}`}
                    style={{
                      width: `${(s.value / g.total) * 100}%`,
                      boxSizing: 'border-box',
                      // A 2px surface gap between fills, taken out of the
                      // segment itself so the row still totals exactly 100%.
                      borderRight: i < segments.length - 1 ? '2px solid #fff' : undefined,
                    }}
                  />
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ComplianceBreakdown;
